import { buffers } from 'redux-saga';
import {
  take,
  takeEvery,
  takeLeading,
  call,
  fork,
  cancel,
  cancelled,
  put,
  actionChannel,
  flush,
} from 'redux-saga/effects';

import ky from 'ky-universal';
import { schema, normalize } from 'normalizr';
import { stringify } from 'query-string';

import { apiHost } from '../../config/api';
import baseFields from '../../config/api/baseFields';
import { AUTH_STATE_LOGIN } from '../../constants';
import authUtils from '../utils/auth';

import {
  LOGOUT,
  AUTH_FLOW_END,
  FETCH_WATCH_HISTORY_REQUESTING,
  FETCH_WATCH_HISTORY_ABORTED,
  FETCH_WATCH_HISTORY_FAILED,
  FETCH_WATCH_HISTORY_SUCCEEDED,
  UPDATE_WATCH_HISTORY,
  UPDATE_WATCH_HISTORY_REQUESTING,
  UPDATE_WATCH_HISTORY_SUCCEEDED,
  UPDATE_WATCH_HISTORY_FAILED,
  UPDATE_WATCH_HISTORY_ABORTED,
  DELETE_WATCH_HISTORY,
  DELETE_ALL_WATCH_HISTORY,
} from '../constants/actionTypes';

const endpoint = `${apiHost}/user/watchHistory`;

function* fetchWatchHistory() {
  const abortController = new AbortController();

  try {
    yield put({ type: FETCH_WATCH_HISTORY_REQUESTING });

    const res = yield call(ky.get, endpoint, {
      signal: abortController.signal,
      searchParams: stringify({ ...baseFields, limit: 9999 }),
      headers: { Authorization: authUtils.getAccessToken() },
    });

    const { data } = yield res.json();

    const watchHistory = data.map((item) => {
      const { DramaId: dramaId, Drama, ...restFields } = item;
      const { DramaInfo: dramaInfo, ...restDramaInfo } = Drama;
      return {
        dramaId,
        dramaInfo: {
          ...dramaInfo,
          ...restDramaInfo,
        },
        ...restFields,
      };
    });

    const watchHistorySchema = [new schema.Entity('watchHistory', undefined, { idAttribute: 'dramaId' })];

    yield put({ type: FETCH_WATCH_HISTORY_SUCCEEDED, payload: normalize(watchHistory, watchHistorySchema) });
  } catch (error) {
    yield put({ type: FETCH_WATCH_HISTORY_FAILED, payload: error, error: true });
  } finally {
    if (yield cancelled()) {
      abortController.abort();
      yield put({ type: FETCH_WATCH_HISTORY_ABORTED });
    }
  }
}

export function* fetchWatchHistoryFlow() {
  while (true) {
    const { payload } = yield take(AUTH_FLOW_END);

    if (payload === AUTH_STATE_LOGIN) {
      const task = yield fork(fetchWatchHistory);
      yield take(LOGOUT);
      yield cancel(task);
    }
  }
}

/**
 * @param {AbortSignal} signal
 */
function* updateWatchHistory(signal, action) {
  try {
    yield put({ type: UPDATE_WATCH_HISTORY_REQUESTING });

    yield call(ky.put, endpoint, {
      signal,
      json: { ...baseFields, ...action.payload },
      headers: { Authorization: authUtils.getAccessToken() },
    });

    yield put({
      type: UPDATE_WATCH_HISTORY_SUCCEEDED,
      payload: { ...action.payload, updatedAt: new Date().toISOString() },
    });
  } catch (error) {
    yield put({ type: UPDATE_WATCH_HISTORY_FAILED, payload: error, error: true });
  } finally {
    if (yield cancelled()) {
      yield put({ type: UPDATE_WATCH_HISTORY_ABORTED });
    }
  }
}

export function* updateWatchHistoryFlow() {
  const queue = yield actionChannel(UPDATE_WATCH_HISTORY);

  while (true) {
    const { payload } = yield take(AUTH_FLOW_END);

    if (payload === AUTH_STATE_LOGIN) {
      yield take(FETCH_WATCH_HISTORY_SUCCEEDED);
      const abortController = new AbortController();
      const tasks = yield takeEvery(queue, updateWatchHistory, abortController.signal);
      yield take(LOGOUT);
      yield cancel(tasks);
      abortController.abort();
    }

    yield flush(queue);
  }
}

/**
 * @param {AbortSignal} signal
 */
function* deleteWatchHistory(signal, action) {
  const req = ky.delete(endpoint, {
    signal,
    searchParams: stringify({ ...baseFields, dramaId: action.payload }),
    headers: { Authorization: authUtils.getAccessToken() },
  });

  yield put({ ...action, optimistic: req });
}

export function* deleteWatchHistoryFlow() {
  const queue = yield actionChannel(DELETE_WATCH_HISTORY);

  while (true) {
    const { payload } = yield take(AUTH_FLOW_END);

    if (payload === AUTH_STATE_LOGIN) {
      yield take(FETCH_WATCH_HISTORY_SUCCEEDED);
      const abortController = new AbortController();
      const tasks = yield takeEvery(queue, deleteWatchHistory, abortController.signal);
      yield take(LOGOUT);
      yield cancel(tasks);
      abortController.abort();
    }

    yield flush(queue);
  }
}

/**
 * @param {AbortSignal} signal
 */
function* deleteAllWatchHistory(signal, action) {
  const req = ky.delete(endpoint, {
    signal,
    searchParams: stringify({ ...baseFields, all: true }),
    headers: { Authorization: authUtils.getAccessToken() },
  });

  yield put({ ...action, optimistic: req });
}

export function* deleteAllWatchHistoryFlow() {
  const queue = yield actionChannel(DELETE_ALL_WATCH_HISTORY, buffers.dropping(1));

  while (true) {
    const { payload } = yield take(AUTH_FLOW_END);

    if (payload === AUTH_STATE_LOGIN) {
      yield take(FETCH_WATCH_HISTORY_SUCCEEDED);
      const abortController = new AbortController();
      const task = yield takeLeading(queue, deleteAllWatchHistory, abortController.signal);
      yield take(LOGOUT);
      yield cancel(task);
      abortController.abort();
    }

    yield flush(queue);
  }
}
